import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject
} from 'react';
import type { TerminalTouchGestureRef } from './TerminalSelection';

export type TerminalShortcut =
  | 'escape'
  | 'slash'
  | 'minus'
  | 'home'
  | 'up'
  | 'end'
  | 'pageUp'
  | 'tab'
  | 'left'
  | 'down'
  | 'right'
  | 'pageDown';

export type TerminalModifiers = {
  ctrl: boolean;
  alt: boolean;
};

export type TerminalScrollMode = 'history' | 'application';

const TERMINAL_SCROLL_MODE_STORAGE_KEY = 'mission-control.terminal-scroll-mode';
const LEGACY_TERMINAL_SCROLL_MODE_STORAGE_KEY = 'proj-creator.terminal-scroll-mode';

type TerminalSpecialKeyToolbarProps = {
  debugAction?: TerminalToolbarAction;
  disabled?: boolean;
  /** Must be called only from a trusted button event, never a repeat timer. */
  onFocus: () => void;
  modifiers: TerminalModifiers;
  onShortcut: (key: TerminalShortcut) => void;
  onToggleModifier: (modifier: keyof TerminalModifiers) => void;
  workspaceActions?: TerminalToolbarAction[];
};

export type TerminalToolbarAction = {
  accessibleLabel: string;
  disabled?: boolean;
  label: ReactNode;
  onActivate: () => void;
  pressed?: boolean;
  title?: string;
};

const SHORTCUTS: Array<{ key: TerminalShortcut; label: string; accessibleLabel: string }> = [
  { key: 'escape', label: 'Esc', accessibleLabel: 'Escape' },
  { key: 'slash', label: '/', accessibleLabel: 'Slash' },
  { key: 'minus', label: '-', accessibleLabel: 'Minus' },
  { key: 'home', label: 'Home', accessibleLabel: 'Home' },
  { key: 'up', label: '↑', accessibleLabel: 'Arrow up' },
  { key: 'end', label: 'End', accessibleLabel: 'End' },
  { key: 'pageUp', label: 'PgUp', accessibleLabel: 'Page up' },
  { key: 'tab', label: 'Tab', accessibleLabel: 'Tab' },
  { key: 'left', label: '←', accessibleLabel: 'Arrow left' },
  { key: 'down', label: '↓', accessibleLabel: 'Arrow down' },
  { key: 'right', label: '→', accessibleLabel: 'Arrow right' },
  { key: 'pageDown', label: 'PgDn', accessibleLabel: 'Page down' }
];

/** Match normal keyboard feel without flooding a PTY when a soft key is held. */
export const TERMINAL_SHORTCUT_REPEAT_INITIAL_DELAY_MS = 350;
export const TERMINAL_SHORTCUT_REPEAT_INTERVAL_MS = 45;

const REPEATABLE_SHORTCUTS = new Set<TerminalShortcut>([
  'home',
  'up',
  'end',
  'pageUp',
  'left',
  'down',
  'right',
  'pageDown'
]);

export function terminalShortcutRepeats(key: TerminalShortcut): boolean {
  return REPEATABLE_SHORTCUTS.has(key);
}

const PLAIN_SEQUENCES: Record<TerminalShortcut, string> = {
  escape: '\u001b',
  slash: '/',
  minus: '-',
  home: '\u001b[H',
  up: '\u001b[A',
  end: '\u001b[F',
  pageUp: '\u001b[5~',
  tab: '\t',
  left: '\u001b[D',
  down: '\u001b[B',
  right: '\u001b[C',
  pageDown: '\u001b[6~'
};

const MODIFIER_FINALS: Partial<Record<TerminalShortcut, string>> = {
  home: 'H',
  up: 'A',
  end: 'F',
  left: 'D',
  down: 'B',
  right: 'C'
};

const PAGE_NUMBERS: Partial<Record<TerminalShortcut, number>> = {
  pageUp: 5,
  pageDown: 6
};

/**
 * A browser's address bar can change the visual viewport by a few dozen CSS
 * pixels. Native keyboards are substantially taller, so require both this
 * floor and a relative change from the last keyboard-free viewport.
 */
export const MOBILE_KEYBOARD_HEIGHT_THRESHOLD_PX = 120;

/**
 * Android fullscreen Chrome can leave Gboard's 24px top accessory strip over
 * the bottom of an otherwise correctly resized VisualViewport. The browser's
 * Virtual Keyboard geometry is not usable as a replacement on affected
 * builds: it becomes empty while the keyboard remains visible.
 */
export const ANDROID_FULLSCREEN_IME_OVERDRAW_GUARD_PX = 24;

export type TerminalViewportMetrics = {
  height: number;
  width: number;
  offsetTop: number;
  offsetLeft: number;
};

export type TerminalViewportBaseline = Pick<TerminalViewportMetrics, 'height' | 'width'>;

export type TerminalViewportState = {
  baseline: TerminalViewportBaseline;
  isKeyboardOpen: boolean;
  keyboardHeight: number;
};

export type TerminalViewportContainerMetrics = {
  height: number;
  width: number;
  top: number;
  left: number;
};

export type TerminalViewportFrameLayout = 'container' | 'overlay';

/**
 * Android and iOS animate the keyboard and the visual-viewport offset over
 * several hundred milliseconds, and the final resize/scroll event can still
 * carry mid-animation metrics. Re-sample the viewport once more after these
 * delays so the maximized frame and the keypad land on the settled geometry
 * instead of keeping a constant few-pixel offset that clips the keypad.
 */
export const TERMINAL_VIEWPORT_SETTLE_DELAYS_MS: readonly number[] = Object.freeze([120, 350, 800]);

type ScrollableTerminal = {
  rows: number;
  scrollLines: (amount: number) => void;
  element?: HTMLElement;
  /**
   * Public xterm input API. Unlike focus(), this emits terminal data without
   * asking the browser to focus xterm's hidden textarea or show the IME.
   */
  input: (data: string) => void;
  buffer: {
    active: {
      type: 'normal' | 'alternate';
    };
  };
  modes: {
    applicationCursorKeysMode: boolean;
    mouseTrackingMode: 'none' | 'x10' | 'vt200' | 'drag' | 'any';
  };
};

type FocusableTerminal = {
  focus: () => void;
  textarea?: HTMLTextAreaElement;
};

/**
 * Keeps focus requests tied to a user gesture. In particular, xterm's focus
 * method asks the browser to focus its hidden textarea, which Android may
 * interpret as a request to show or re-show the native keyboard. Calling it
 * repeatedly from a held-key timer makes that keyboard state unreliable.
 */
export function focusTerminalIfNeeded(
  terminal: FocusableTerminal | null | undefined,
  activeElement?: Element | null
): boolean {
  if (!terminal) return false;
  const textarea = terminal.textarea;
  const currentActiveElement = activeElement === undefined
    ? textarea?.ownerDocument?.activeElement ?? null
    : activeElement;
  if (textarea && currentActiveElement === textarea) return false;
  terminal.focus();
  return true;
}

/**
 * Recovers xterm focus after a browser/app blur when the user next taps the
 * terminal surface. Waiting for the trusted click instead of focusing on raw
 * pointer-down keeps swipe scrolling and long-press selection from reopening
 * a keyboard the user intentionally dismissed.
 */
export function useTerminalFocusRecovery<T extends HTMLElement>(
  hostRef: RefObject<T | null>,
  terminalRef: RefObject<FocusableTerminal | null>,
  enabled = true
) {
  const focusTerminal = useCallback(
    () => focusTerminalIfNeeded(terminalRef.current),
    [terminalRef]
  );

  useEffect(() => {
    if (!enabled) return;
    const host = hostRef.current;
    if (!host) return;
    const recoverFocus = (event: Event) => {
      // Synthetic events must not cause a delayed/programmatic keyboard
      // request. Real touch/click interaction is the safe focus boundary.
      if (!event.isTrusted) return;
      focusTerminal();
    };
    host.addEventListener('click', recoverFocus, { capture: true, passive: true });
    return () => host.removeEventListener('click', recoverFocus, true);
  }, [enabled, focusTerminal, hostRef]);

  return focusTerminal;
}

export type TerminalTouchScrollDelta = {
  lines: number;
  remainder: number;
};

/** Converts a finger movement to whole xterm rows without losing small moves. */
export function terminalTouchScrollDelta(
  deltaPixels: number,
  remainder: number,
  pixelsPerLine: number
): TerminalTouchScrollDelta {
  const total = deltaPixels + remainder;
  const safePixelsPerLine = Math.max(1, pixelsPerLine);
  const roundedLines = total < 0
    ? Math.ceil(total / safePixelsPerLine)
    : Math.floor(total / safePixelsPerLine);
  const lines = Object.is(roundedLines, -0) ? 0 : roundedLines;
  return { lines, remainder: total - lines * safePixelsPerLine };
}

export type TerminalTouchScrollAction =
  | { type: 'scroll'; lines: number }
  | { type: 'input'; sequences: string[] }
  | { type: 'wheel'; deltas: number[] }
  | { type: 'none' };

type TerminalScreenBounds = Pick<DOMRect, 'bottom' | 'height' | 'top'>;

/**
 * Keeps a touch-generated wheel event out of the terminal's final row.
 * tmux reserves that row for its status line and binds WheelUpStatus /
 * WheelDownStatus to previous-window / next-window. A swipe that happens to
 * end there must continue scrolling pane history instead of switching windows.
 */
export function terminalTouchWheelClientY(
  clientY: number,
  screen: TerminalScreenBounds,
  rows: number
): number {
  if (
    !Number.isFinite(clientY) ||
    !Number.isFinite(screen.top) ||
    !Number.isFinite(screen.bottom) ||
    !Number.isFinite(screen.height) ||
    screen.height <= 0 ||
    rows < 1
  ) {
    return clientY;
  }

  const rowHeight = screen.height / Math.max(1, Math.floor(rows));
  const firstRowCenter = screen.top + rowHeight / 2;
  const penultimateRowCenter = screen.bottom - rowHeight * 1.5;
  const safeBottom = Math.max(firstRowCenter, penultimateRowCenter);
  return Math.min(safeBottom, Math.max(firstRowCenter, clientY));
}

/**
 * Claims vertical touch gestures before the browser can turn them into page
 * panning. The inline fallback keeps the hook reliable if an embedding page
 * omits or overrides the terminal stylesheet.
 */
export function claimTerminalTouchGestures(host: Pick<HTMLElement, 'style'>): () => void {
  const previousTouchAction = host.style.touchAction;
  const previousOverscrollBehavior = host.style.overscrollBehavior;
  host.style.touchAction = 'none';
  host.style.overscrollBehavior = 'contain';
  return () => {
    host.style.touchAction = previousTouchAction;
    host.style.overscrollBehavior = previousOverscrollBehavior;
  };
}

/**
 * Matches Termux's safe alternate-buffer fallback: ordinary local scrollback
 * remains local, while a terminal application that owns its own history gets
 * cursor input only if it has not claimed mouse reporting. This deliberately
 * does not guess that GNU Screen is running or enter its copy mode; users
 * enter that mode themselves and then a swipe supplies its Up/Down commands.
 */
export function terminalTouchScrollAction(
  lines: number,
  terminal: Pick<ScrollableTerminal, 'buffer' | 'modes'>,
  mode: TerminalScrollMode = 'history'
): TerminalTouchScrollAction {
  if (!lines) return { type: 'none' };

  const applicationSequence = () => `\u001b${terminal.modes.applicationCursorKeysMode ? 'O' : '['}${
    lines < 0 ? 'A' : 'B'
  }`;

  // GNU Screen and similar full-screen programs can opt into row-based
  // application keys. This is explicit so an ordinary shell swipe never
  // mutates command history by pretending to be Arrow Up/Down.
  if (mode === 'application' && terminal.buffer.active.type === 'alternate') {
    const sequence = applicationSequence();
    return { type: 'input', sequences: Array.from({ length: Math.abs(lines) }, () => sequence) };
  }

  // Let xterm encode mouse reports from ordinary WheelEvents. Its mouse
  // service owns the active protocol, coordinate encoding, and private modes;
  // reproducing those escape sequences in this hook would be brittle.
  if (terminal.modes.mouseTrackingMode !== 'none') {
    const delta = lines < 0 ? -1 : 1;
    return { type: 'wheel', deltas: Array.from({ length: Math.abs(lines) }, () => delta) };
  }

  if (terminal.buffer.active.type === 'normal') {
    return { type: 'scroll', lines };
  }

  // tmux advertises mouse tracking for its per-pane history. If that is not
  // available, stay local instead of falling back to shell arrow input.
  return { type: 'scroll', lines };
}

export function useTerminalScrollMode() {
  const [scrollMode, setScrollModeState] = useState<TerminalScrollMode>(() => {
    try {
      return readTerminalScrollMode(window.localStorage);
    } catch {
      return 'history';
    }
  });
  const setScrollMode = useCallback((mode: TerminalScrollMode) => {
    setScrollModeState(mode);
    try {
      window.localStorage.setItem(TERMINAL_SCROLL_MODE_STORAGE_KEY, mode);
    } catch {
      // Private browsing and embedded webviews can disable local storage.
    }
  }, []);
  return { scrollMode, setScrollMode };
}

export function readTerminalScrollMode(storage: Pick<Storage, 'getItem' | 'setItem'>): TerminalScrollMode {
  const canonical = storage.getItem(TERMINAL_SCROLL_MODE_STORAGE_KEY);
  if (canonical === 'application' || canonical === 'history') return canonical;
  const legacy = storage.getItem(LEGACY_TERMINAL_SCROLL_MODE_STORAGE_KEY);
  if (legacy === 'application' || legacy === 'history') {
    try {
      storage.setItem(TERMINAL_SCROLL_MODE_STORAGE_KEY, legacy);
    } catch {
      // Retain the old preference when storage is read-only.
    }
    return legacy;
  }
  return 'history';
}

type TerminalScrollModeControlProps = {
  mode: TerminalScrollMode;
  onChange: (mode: TerminalScrollMode) => void;
};

export function TerminalScrollModeControl({ mode, onChange }: TerminalScrollModeControlProps) {
  return (
    <label className="terminal-panel__scroll-mode">
      <span>Scroll</span>
      <select
        aria-label="Terminal scroll behavior"
        value={mode}
        onChange={(event) => onChange(event.target.value as TerminalScrollMode)}
      >
        <option value="history">History</option>
        <option value="application">App keys</option>
      </select>
    </label>
  );
}

/**
 * Enables predictable row-based one-finger scrolling in the embedded and
 * maximized terminal. Capture listeners claim a gesture once it crosses a terminal row,
 * preventing xterm from processing any later part of that same swipe. Start/
 * end are never canceled, so a stationary long-press can remain available
 * where the browser permits it; clipboard copy/paste events remain entirely
 * owned by xterm's handlers.
 */
export function useTerminalTouchScroll<T extends HTMLElement>(
  hostRef: RefObject<T | null>,
  terminalRef: RefObject<ScrollableTerminal | null>,
  enabled: boolean,
  onScrollLines?: (lines: number) => void,
  mode: TerminalScrollMode = 'history',
  gestureOwnerRef?: TerminalTouchGestureRef
) {
  useEffect(() => {
    if (!enabled) return;
    const host = hostRef.current;
    if (!host) return;
    const releaseTouchGestures = claimTerminalTouchGestures(host);
    const gestureOwner: TerminalTouchGestureRef = gestureOwnerRef ?? { current: 'idle' };

    let lastY: number | null = null;
    let remainder = 0;
    let claimed = false;
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        lastY = null;
        remainder = 0;
        claimed = false;
        return;
      }
      lastY = event.touches[0].clientY;
      remainder = 0;
      claimed = false;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        lastY = null;
        remainder = 0;
        claimed = false;
        return;
      }
      if (gestureOwner.current === 'selection') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (claimed) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (lastY === null) {
        lastY = event.touches[0].clientY;
        return;
      }

      const terminal = terminalRef.current;
      const nextY = event.touches[0].clientY;
      if (terminal) {
        const pixelsPerLine = Math.max(8, host.clientHeight / Math.max(1, terminal.rows));
        const scroll = terminalTouchScrollDelta(lastY - nextY, remainder, pixelsPerLine);
        remainder = scroll.remainder;
        if (scroll.lines) {
          const action = terminalTouchScrollAction(scroll.lines, terminal, mode);
          let handled = false;
          if (action.type === 'scroll') {
            gestureOwner.current = 'scroll';
            if (onScrollLines) {
              onScrollLines(action.lines);
            } else {
              terminal.scrollLines(action.lines);
            }
            handled = true;
          } else if (action.type === 'input') {
            gestureOwner.current = 'scroll';
            // One public xterm input call per row preserves native/Termux-like
            // key events for Screen copy mode and other alternate-buffer apps.
            // Crucially, input() does not focus xterm's textarea.
            for (const sequence of action.sequences) terminal.input(sequence);
            handled = true;
          } else if (action.type === 'wheel' && terminal.element) {
            gestureOwner.current = 'scroll';
            // Dispatch through xterm's public DOM surface so its current mouse
            // protocol and cell coordinates determine the exact PTY frames.
            // Keep synthetic scrolling above the final terminal row: tmux's
            // default status-row wheel bindings switch windows there.
            const touch = event.touches[0];
            const screen = terminal.element.querySelector<HTMLElement>('.xterm-screen')
              ?? terminal.element;
            const clientY = terminalTouchWheelClientY(
              touch.clientY,
              screen.getBoundingClientRect(),
              terminal.rows
            );
            for (const deltaY of action.deltas) {
              terminal.element.dispatchEvent(new WheelEvent('wheel', {
                bubbles: true,
                cancelable: true,
                clientX: touch.clientX,
                clientY,
                deltaMode: WheelEvent.DOM_DELTA_LINE,
                deltaY
              }));
            }
            handled = true;
          }
          if (!handled) {
            lastY = nextY;
            return;
          }
          // Once the gesture has claimed at least one whole terminal row,
          // neither xterm nor the browser may process any remaining movement.
          // Keeping pre-threshold movement untouched preserves long-press
          // selection.
          claimed = true;
          event.preventDefault();
          event.stopPropagation();
        }
      }
      lastY = nextY;
    };
    const onTouchEnd = (event: TouchEvent) => {
      lastY = null;
      remainder = 0;
      claimed = false;
      gestureOwner.current = 'idle';
    };

    // xterm's document handlers can stop bubbling touch events. Capture on
    // its host so fullscreen scroll works even while an alternate buffer (GNU
    // screen, tmux, vim, etc.) owns the visible terminal.
    const capture = true;
    host.addEventListener('touchstart', onTouchStart, { capture, passive: true });
    host.addEventListener('touchmove', onTouchMove, { capture, passive: false });
    host.addEventListener('touchend', onTouchEnd, { capture, passive: true });
    host.addEventListener('touchcancel', onTouchEnd, { capture, passive: true });
    return () => {
      host.removeEventListener('touchstart', onTouchStart, capture);
      host.removeEventListener('touchmove', onTouchMove, capture);
      host.removeEventListener('touchend', onTouchEnd, capture);
      host.removeEventListener('touchcancel', onTouchEnd, capture);
      releaseTouchGestures();
    };
  }, [enabled, gestureOwnerRef, hostRef, mode, onScrollLines, terminalRef]);
}

type TerminalFullscreenExitButtonProps = {
  onExit: () => void;
};

export function TerminalFullscreenExitButton({ onExit }: TerminalFullscreenExitButtonProps) {
  return (
    <button
      type="button"
      className="ghost terminal-panel__fullscreen-exit"
      onClick={onExit}
      aria-label="Exit terminal full screen"
      title="Exit full screen"
    >
      Exit full screen
    </button>
  );
}

/**
 * Converts the optional VisualViewport API into safe CSS-pixel metrics. The
 * window dimensions keep the fixed maximize fallback working in older Safari
 * and embedded webviews that do not implement VisualViewport.
 */
export function terminalViewportMetrics(
  viewport: Partial<TerminalViewportMetrics> | null | undefined,
  fallback: Pick<TerminalViewportMetrics, 'height' | 'width'>
): TerminalViewportMetrics {
  const positive = (value: number | undefined, fallbackValue: number) =>
    Number.isFinite(value) && value! > 0 ? value! : fallbackValue;
  const finite = (value: number | undefined) => Number.isFinite(value) ? value! : 0;
  return {
    height: positive(viewport?.height, fallback.height),
    width: positive(viewport?.width, fallback.width),
    offsetTop: finite(viewport?.offsetTop),
    offsetLeft: finite(viewport?.offsetLeft)
  };
}

/**
 * Resolves VisualViewport coordinates inside the panel that contains the
 * maximized frame. Some Android fullscreen implementations leave that panel
 * at layout-viewport size, while others already resize/reposition it to the
 * visual viewport. Applying offsetTop/offsetLeft verbatim in the latter case
 * double-translates the frame: the outer black shell appears above it and the
 * same number of pixels are clipped from the keypad at the bottom.
 *
 * Clamp the relative offset and frame size to the panel's actual rectangle.
 * A visual-sized panel therefore gets offset 0; a layout-sized panel still
 * follows a genuinely panned VisualViewport.
 */
export function terminalViewportFrameMetrics(
  viewport: TerminalViewportMetrics,
  container: TerminalViewportContainerMetrics | null | undefined
): TerminalViewportMetrics {
  if (!container) return viewport;
  const positive = (value: number, fallback: number) =>
    Number.isFinite(value) && value > 0 ? value : fallback;
  const finite = (value: number) => Number.isFinite(value) ? value : 0;
  const containerHeight = positive(container.height, viewport.height);
  const containerWidth = positive(container.width, viewport.width);
  const frameHeight = Math.min(viewport.height, containerHeight);
  const frameWidth = Math.min(viewport.width, containerWidth);
  const maxTop = Math.max(0, containerHeight - frameHeight);
  const maxLeft = Math.max(0, containerWidth - frameWidth);
  const relativeTop = finite(viewport.offsetTop) - finite(container.top);
  const relativeLeft = finite(viewport.offsetLeft) - finite(container.left);
  const offsetTop = Math.min(maxTop, Math.max(0, relativeTop));
  const offsetLeft = Math.min(maxLeft, Math.max(0, relativeLeft));

  return {
    height: Math.min(frameHeight, containerHeight - offsetTop),
    width: Math.min(frameWidth, containerWidth - offsetLeft),
    offsetTop,
    offsetLeft
  };
}

/**
 * Detects the common mobile case where Chromium has already resized and
 * positioned the fullscreen element to the VisualViewport. Keeping another
 * absolutely-positioned, overflow-clipped layer in that case is redundant and
 * can expose an Android compositor bug: DOM geometry remains correct, while
 * the child layer is painted short and clips the bottom of the soft-key bar.
 *
 * When their dimensions coincide, let the fullscreen element itself own the
 * flex layout. Offsets are intentionally not compared: VisualViewport offsets
 * and getBoundingClientRect positions use different coordinate spaces on some
 * Android builds. Keep the overlay frame only when the fullscreen element
 * still has the larger layout-viewport dimensions.
 */
export function terminalViewportFrameLayout(
  viewport: TerminalViewportMetrics,
  container: TerminalViewportContainerMetrics | null | undefined
): TerminalViewportFrameLayout {
  if (!container) return 'overlay';
  const tolerance = 1;
  const approximatelyEqual = (left: number, right: number) =>
    Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) <= tolerance;
  return approximatelyEqual(container.height, viewport.height) &&
    approximatelyEqual(container.width, viewport.width)
    ? 'container'
    : 'overlay';
}

export function terminalKeyboardOverdrawGuard(
  userAgent: string,
  isFullscreen: boolean,
  isKeyboardOpen: boolean
): number {
  return isFullscreen && isKeyboardOpen && /Android/i.test(userAgent)
    ? ANDROID_FULLSCREEN_IME_OVERDRAW_GUARD_PX
    : 0;
}

export function terminalViewportWithBottomGuard(
  viewport: TerminalViewportMetrics,
  guard: number
): TerminalViewportMetrics {
  const safeGuard = Number.isFinite(guard) ? Math.max(0, guard) : 0;
  if (safeGuard === 0) return viewport;
  return {
    ...viewport,
    height: Math.max(1, viewport.height - safeGuard)
  };
}

/**
 * Uses VisualViewport clipping only for a real software keyboard. During
 * keyboard-free fullscreen entry Chromium can resize the fullscreen element
 * one frame before VisualViewport catches up. Filling the measured panel in
 * that state avoids briefly exposing its black outer shell; on some Android
 * compositors that stale strip otherwise remains visible after the metrics
 * have converged.
 */
export function terminalViewportFrameForKeyboardState(
  viewport: TerminalViewportMetrics,
  container: TerminalViewportContainerMetrics | null | undefined,
  isKeyboardOpen: boolean
): TerminalViewportMetrics {
  if (!container || isKeyboardOpen) return terminalViewportFrameMetrics(viewport, container);
  const positive = (value: number, fallback: number) =>
    Number.isFinite(value) && value > 0 ? value : fallback;
  return {
    height: positive(container.height, viewport.height),
    width: positive(container.width, viewport.width),
    offsetTop: 0,
    offsetLeft: 0
  };
}

/**
 * Updates the keyboard-free viewport anchor and identifies a real software
 * keyboard. A large width change is an orientation/layout transition, not a
 * keyboard, and therefore resets the anchor before doing the comparison.
 */
export function terminalViewportState(
  viewport: TerminalViewportMetrics,
  baseline: TerminalViewportBaseline | null,
  isCoarsePointer: boolean
): TerminalViewportState {
  const initialBaseline = baseline ?? { height: viewport.height, width: viewport.width };
  const isOrientationOrLayoutChange = Math.abs(initialBaseline.width - viewport.width) > 24;
  const activeBaseline = isOrientationOrLayoutChange
    ? { height: viewport.height, width: viewport.width }
    : {
      // Do not replace the anchor for small, incremental VisualViewport
      // reductions: Android and iOS often animate a keyboard in several such
      // steps. Only a larger recovered viewport becomes the new anchor.
      height: Math.max(initialBaseline.height, viewport.height),
      width: initialBaseline.width
    };
  const keyboardHeight = Math.max(0, activeBaseline.height - viewport.height);
  const threshold = Math.max(
    MOBILE_KEYBOARD_HEIGHT_THRESHOLD_PX,
    Math.round(activeBaseline.height * 0.2)
  );
  const isKeyboardOpen = isCoarsePointer && keyboardHeight >= threshold;

  return {
    // Keep the largest keyboard-free viewport. This accommodates mobile
    // browser chrome expanding/collapsing without losing the anchor partway
    // through a native keyboard's open animation.
    baseline: activeBaseline,
    isKeyboardOpen,
    keyboardHeight: isKeyboardOpen ? keyboardHeight : 0
  };
}

function modifierNumber({ ctrl, alt }: TerminalModifiers) {
  // This follows xterm's CSI modifier encoding: Shift=2, Alt=3, Ctrl=5,
  // Ctrl+Alt=7. The toolbar deliberately provides only Ctrl and Alt.
  if (ctrl && alt) return 7;
  if (ctrl) return 5;
  return 3;
}

/** Encodes the same terminal escapes that xterm emits for these special keys. */
export function terminalShortcutSequence(key: TerminalShortcut, modifiers: TerminalModifiers): string {
  if (!modifiers.ctrl && !modifiers.alt) return PLAIN_SEQUENCES[key];

  const modifier = modifierNumber(modifiers);
  const final = MODIFIER_FINALS[key];
  if (final) return `\u001b[1;${modifier}${final}`;

  const pageNumber = PAGE_NUMBERS[key];
  if (pageNumber) return `\u001b[${pageNumber};${modifier}~`;

  if ((key === 'minus' || key === 'slash') && modifiers.ctrl) {
    // Ctrl+- and Ctrl+/ are ASCII Unit Separator; Alt adds the conventional
    // ESC prefix. This mirrors the character produced by physical keyboards.
    return `${modifiers.alt ? '\u001b' : ''}\u001f`;
  }
  if (key === 'tab' && modifiers.ctrl) {
    // Ctrl+Tab has no universally-supported CSI form. xterm-compatible apps
    // recognise this modifier form, while Alt still gets the ESC prefix below.
    return `\u001b[27;${modifier};9~`;
  }
  // Escape and Alt+Tab are conventionally represented by an ESC prefix.
  return `${modifiers.alt ? '\u001b' : ''}${PLAIN_SEQUENCES[key]}`;
}

/** Applies the one-shot soft-key modifiers to the first ordinary input byte. */
export function terminalInputWithModifiers(data: string, modifiers: TerminalModifiers): string {
  if (!data || (!modifiers.ctrl && !modifiers.alt)) return data;

  const first = data[0];
  let transformed = first;
  if (modifiers.ctrl) {
    const code = first.toUpperCase().charCodeAt(0);
    transformed = first === ' '
      // Ctrl-Space is NUL, which is tmux's default begin-selection binding in
      // emacs copy mode. GBoard supplies the ordinary space and the sticky
      // Ctrl toolbar modifier supplies this control encoding.
      ? '\u0000'
      : first === '-' || first === '/'
        ? '\u001f'
        : (code >= 64 && code <= 95 ? String.fromCharCode(0x1f & code) : first);
  }
  return `${modifiers.alt ? '\u001b' : ''}${transformed}${data.slice(1)}`;
}

/**
 * Next sticky-modifier state after the user taps one of the Ctrl/Alt soft
 * keys. Tapping the latched key again unlatches only that modifier.
 */
export function toggledTerminalModifier(
  modifiers: TerminalModifiers,
  modifier: keyof TerminalModifiers
): TerminalModifiers {
  return { ...modifiers, [modifier]: !modifiers[modifier] };
}

/**
 * Modifier state that remains once a toolbar shortcut or ordinary input has
 * been delivered: the sticky Ctrl/Alt latch applies to exactly one key. An
 * unmodified delivery returns the same object so held-key repeats keep the
 * hook's no-rerender fast path.
 */
export function terminalModifiersAfterDelivery(modifiers: TerminalModifiers): TerminalModifiers {
  return modifiers.ctrl || modifiers.alt ? { ctrl: false, alt: false } : modifiers;
}

/** Keeps Ctrl and Alt available for one soft-keyboard/xterm input or toolbar key. */
export function useTerminalSoftKeyModifiers() {
  const [modifiers, setModifiers] = useState<TerminalModifiers>({ ctrl: false, alt: false });
  const modifiersRef = useRef(modifiers);

  const replaceModifiers = useCallback((next: TerminalModifiers) => {
    modifiersRef.current = next;
    setModifiers(next);
  }, []);
  const toggleModifier = useCallback((modifier: keyof TerminalModifiers) => {
    replaceModifiers(toggledTerminalModifier(modifiersRef.current, modifier));
  }, [replaceModifiers]);
  const consumeShortcut = useCallback((key: TerminalShortcut) => {
    const current = modifiersRef.current;
    const data = terminalShortcutSequence(key, current);
    // An unmodified held navigation key can repeat dozens of times per second.
    // Avoid a React state update for every PTY frame when there is no sticky
    // modifier to consume.
    if (current.ctrl || current.alt) {
      replaceModifiers(terminalModifiersAfterDelivery(current));
    }
    return data;
  }, [replaceModifiers]);
  const transformInput = useCallback((data: string) => {
    const current = modifiersRef.current;
    if (!current.ctrl && !current.alt) return data;
    replaceModifiers(terminalModifiersAfterDelivery(current));
    return terminalInputWithModifiers(data, current);
  }, [replaceModifiers]);

  return { modifiers, toggleModifier, consumeShortcut, transformInput };
}

/**
 * Runs native-key-like repeat for a held navigation soft key. Pointer capture
 * keeps the key held when a finger or mouse leaves the button; every way the
 * pointer can end (including a browser blur) clears both timers.
 */
function useTerminalShortcutRepeat(
  sendShortcut: (key: TerminalShortcut) => void,
  focusTerminal: () => void,
  disabled: boolean
) {
  const activePointerIdRef = useRef<number | null>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const repeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback((pointerId?: number) => {
    if (pointerId !== undefined && activePointerIdRef.current !== pointerId) return;
    if (delayTimerRef.current !== null) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (repeatTimerRef.current !== null) {
      clearInterval(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    activePointerIdRef.current = null;
  }, []);

  const stopOnWindowBlur = useCallback(() => stop(), [stop]);

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.visibilityState !== 'visible') stop();
    };
    const stopOnPointerEnd = (event: globalThis.PointerEvent) => stop(event.pointerId);
    window.addEventListener('blur', stopOnWindowBlur);
    document.addEventListener('visibilitychange', stopWhenHidden);
    // Pointer capture is supported by current Android/iOS browsers, but a
    // global end guard prevents a runaway PTY repeat if capture is denied or
    // is lost while the finger finishes outside the key.
    window.addEventListener('pointerup', stopOnPointerEnd);
    window.addEventListener('pointercancel', stopOnPointerEnd);
    return () => {
      window.removeEventListener('blur', stopOnWindowBlur);
      document.removeEventListener('visibilitychange', stopWhenHidden);
      window.removeEventListener('pointerup', stopOnPointerEnd);
      window.removeEventListener('pointercancel', stopOnPointerEnd);
      stop();
    };
  }, [stop, stopOnWindowBlur]);

  useEffect(() => {
    if (disabled) stop();
  }, [disabled, stop]);

  const start = useCallback((
    event: PointerEvent<HTMLButtonElement>,
    key: TerminalShortcut,
    allowRepeat: boolean
  ) => {
    // Focus exactly once during the trusted pointer-down, then send precisely
    // one initial key press immediately. The delayed repeat intentionally
    // sends bytes only: refocusing from a timer can dismiss Android's keyboard.
    event.preventDefault();
    if (disabled || !terminalShortcutRepeats(key)) return;

    const pointerId = event.pointerId;
    stop();
    focusTerminal();
    sendShortcut(key);
    if (!allowRepeat) return;

    activePointerIdRef.current = pointerId;
    try {
      event.currentTarget.setPointerCapture(pointerId);
    } catch {
      // Pointer capture is unavailable for synthetic events in some browsers;
      // pointerup/cancel and the window-blur guard still stop the repeat.
    }
    delayTimerRef.current = setTimeout(() => {
      if (activePointerIdRef.current !== pointerId) return;
      sendShortcut(key);
      repeatTimerRef.current = setInterval(() => {
        if (activePointerIdRef.current === pointerId) sendShortcut(key);
      }, TERMINAL_SHORTCUT_REPEAT_INTERVAL_MS);
    }, TERMINAL_SHORTCUT_REPEAT_INITIAL_DELAY_MS);
  }, [disabled, focusTerminal, sendShortcut, stop]);

  const onClick = useCallback((event: MouseEvent<HTMLButtonElement>, key: TerminalShortcut) => {
    // Pointer presses already sent their initial key from pointerdown. Keyboard
    // and assistive-technology activation reports detail 0 and remains a
    // regular single press.
    if (event.detail > 0) {
      event.preventDefault();
      return;
    }
    // Keyboard and assistive activation do not have pointerdown, so this is
    // their one trusted focus request and one-shot terminal input.
    focusTerminal();
    sendShortcut(key);
  }, [focusTerminal, sendShortcut]);

  return { start, stop, onClick };
}

/**
 * A compact, touch-friendly two-row keypad for mobile terminal users. Ctrl
 * and Alt are sticky one-shot modifiers: select either, then choose a special
 * key. A terminal workspace keeps its two navigation actions in the existing
 * two rows: Sessions follows PgUp and Next follows PgDn.
 */
export function TerminalSpecialKeyToolbar({
  debugAction,
  disabled = false,
  onFocus,
  modifiers,
  onShortcut,
  onToggleModifier,
  workspaceActions = []
}: TerminalSpecialKeyToolbarProps) {
  const toggleModifier = (modifier: keyof TerminalModifiers) => {
    onFocus();
    onToggleModifier(modifier);
  };

  const sendShortcut = useCallback((key: TerminalShortcut) => {
    onShortcut(key);
  }, [onShortcut]);

  const { start: startRepeat, stop: stopRepeat, onClick: sendRepeatableShortcut } =
    useTerminalShortcutRepeat(sendShortcut, onFocus, disabled);

  const keepTerminalFocus = (event: PointerEvent<HTMLButtonElement>) => {
    // Do not make a touch/click on the toolbar steal xterm's keyboard focus.
    event.preventDefault();
  };

  const shortcutButton = ({ key, label, accessibleLabel }: typeof SHORTCUTS[number]) => {
    const repeats = terminalShortcutRepeats(key);
    const allowRepeat = repeats && !modifiers.ctrl && !modifiers.alt;
    return (
      <button
        key={key}
        type="button"
        className="terminal-panel__shortcut"
        aria-label={accessibleLabel}
        title={accessibleLabel}
        disabled={disabled}
        onPointerDown={
          repeats ? (event) => startRepeat(event, key, allowRepeat) : keepTerminalFocus
        }
        onPointerUp={repeats ? (event) => stopRepeat(event.pointerId) : undefined}
        onPointerCancel={repeats ? (event) => stopRepeat(event.pointerId) : undefined}
        onLostPointerCapture={repeats ? (event) => stopRepeat(event.pointerId) : undefined}
        onClick={
          repeats
            ? (event) => sendRepeatableShortcut(event, key)
            : () => {
                onFocus();
                sendShortcut(key);
              }
        }
      >
        {label}
      </button>
    );
  };

  const workspaceActionButton = (action: TerminalToolbarAction) => (
    <button
      key={action.accessibleLabel}
      type="button"
      className="terminal-panel__shortcut terminal-panel__shortcut--workspace"
      aria-label={action.accessibleLabel}
      aria-pressed={action.pressed}
      title={action.title ?? action.accessibleLabel}
      disabled={action.disabled}
      onPointerDown={keepTerminalFocus}
      onClick={action.onActivate}
    >
      {action.label}
    </button>
  );

  const debugActionButton = debugAction ? (
    <button
      key={debugAction.accessibleLabel}
      type="button"
      className="terminal-panel__shortcut terminal-panel__shortcut--workspace terminal-panel__shortcut--debug"
      aria-label={debugAction.accessibleLabel}
      title={debugAction.title ?? debugAction.accessibleLabel}
      disabled={disabled || debugAction.disabled}
      onPointerDown={keepTerminalFocus}
      onClick={debugAction.onActivate}
    >
      {debugAction.label}
    </button>
  ) : null;

  const modifierButton = (
    modifier: keyof TerminalModifiers,
    label: string,
    accessibleLabel: string,
    title: string
  ) => {
    const latched = modifiers[modifier];
    return (
      <button
        key={modifier}
        type="button"
        className="terminal-panel__shortcut terminal-panel__shortcut--modifier"
        aria-pressed={latched}
        aria-label={accessibleLabel}
        title={title}
        disabled={disabled}
        onPointerDown={keepTerminalFocus}
        onClick={() => toggleModifier(modifier)}
      >
        {/* The latched class carries the non-color active cue (underlined,
            heavier label plus an inset ring in styles.css); aria-pressed keeps
            the state exposed to assistive technology. */}
        <span
          className={`terminal-panel__shortcut-label${latched ? ' terminal-panel__shortcut-label--latched' : ''}`}
        >
          {label}
        </span>
      </button>
    );
  };

  return (
    <div
      className={`terminal-panel__shortcut-bar${workspaceActions.length ? ' terminal-panel__shortcut-bar--workspace' : ''}${debugAction ? ' terminal-panel__shortcut-bar--debug' : ''}`}
      role="toolbar"
      aria-label="Terminal special keys"
    >
      {workspaceActions.length ? (
        <>
          {debugActionButton}
          {SHORTCUTS.slice(0, 7).map(shortcutButton)}
          {workspaceActions.slice(0, 1).map(workspaceActionButton)}
          {SHORTCUTS.slice(7, 8).map(shortcutButton)}
          {modifierButton('ctrl', 'Ctrl', 'Control modifier', 'Control modifier for the next terminal key')}
          {modifierButton('alt', 'Alt', 'Alt modifier', 'Alt modifier for the next terminal key')}
          {SHORTCUTS.slice(8).map(shortcutButton)}
          {workspaceActions.slice(1).map(workspaceActionButton)}
        </>
      ) : (
        <>
          {debugActionButton}
          {SHORTCUTS.slice(0, 8).map(shortcutButton)}
          {modifierButton('ctrl', 'Ctrl', 'Control modifier', 'Control modifier for the next terminal key')}
          {modifierButton('alt', 'Alt', 'Alt modifier', 'Alt modifier for the next terminal key')}
          {SHORTCUTS.slice(8).map(shortcutButton)}
        </>
      )}
    </div>
  );
}

/**
 * Makes the terminal panel occupy the viewport even where the browser
 * Fullscreen API is unavailable or declined. While maximized it follows the
 * VisualViewport, making both xterm's last line and the keypad stay above an
 * Android/iOS native keyboard. When supported it also requests browser
 * fullscreen, which removes browser chrome.
 */
export function useTerminalMaximize<T extends HTMLElement>() {
  const panelRef = useRef<T | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMobileKeyboardOpen, setIsMobileKeyboardOpen] = useState(false);
  const [visualViewport, setVisualViewport] = useState<TerminalViewportMetrics | null>(null);
  const [layoutViewportHeight, setLayoutViewportHeight] = useState<number | null>(null);
  const [keyboardOverdrawGuard, setKeyboardOverdrawGuard] = useState(0);
  const [viewportFrameLayout, setViewportFrameLayout] =
    useState<TerminalViewportFrameLayout>('overlay');

  useEffect(() => {
    if (!isMaximized) {
      return;
    }

    const media = window.matchMedia?.('(pointer: coarse)');
    let baseline: TerminalViewportBaseline | null = null;
    let lastAppliedKey: string | null = null;
    let settleTimers: ReturnType<typeof setTimeout>[] = [];

    const updateViewport = () => {
      const metrics = terminalViewportMetrics(window.visualViewport, {
        height: window.innerHeight,
        width: window.innerWidth
      });
      const state = terminalViewportState(metrics, baseline, Boolean(media?.matches));
      baseline = state.baseline;
      const panelRect = panelRef.current?.getBoundingClientRect();
      const containerMetrics = panelRect ? {
        height: panelRect.height,
        width: panelRect.width,
        top: panelRect.top,
        left: panelRect.left
      } : null;
      const baseFrameMetrics = terminalViewportFrameForKeyboardState(
        metrics,
        containerMetrics,
        state.isKeyboardOpen
      );
      const nextKeyboardOverdrawGuard = terminalKeyboardOverdrawGuard(
        navigator.userAgent,
        document.fullscreenElement === panelRef.current,
        state.isKeyboardOpen
      );
      const frameMetrics = terminalViewportWithBottomGuard(
        baseFrameMetrics,
        nextKeyboardOverdrawGuard
      );
      const frameLayout = terminalViewportFrameLayout(
        nextKeyboardOverdrawGuard > 0 ? frameMetrics : metrics,
        containerMetrics
      );

      // Settle re-syncs intentionally re-read the metrics; skip the state
      // write only when they still match the last successfully applied sample.
      const appliedKey = `${metrics.width}x${metrics.height}+${metrics.offsetTop}+${metrics.offsetLeft}` +
        `>${frameMetrics.width}x${frameMetrics.height}+${frameMetrics.offsetTop}+${frameMetrics.offsetLeft}` +
        `/${window.innerHeight}/${state.isKeyboardOpen ? 'k' : 'c'}/${frameLayout}` +
        `/guard=${nextKeyboardOverdrawGuard}`;
      if (appliedKey === lastAppliedKey) return;
      lastAppliedKey = appliedKey;

      setIsMobileKeyboardOpen(state.isKeyboardOpen);
      setVisualViewport(frameMetrics);
      setLayoutViewportHeight(window.innerHeight);
      setKeyboardOverdrawGuard(nextKeyboardOverdrawGuard);
      setViewportFrameLayout(frameLayout);
    };

    // Re-samples the viewport after the current keyboard/viewport transition
    // should have settled. The double rAF waits out the frame that applies the
    // last animation step; bounded timers cover browsers that throttle or skip
    // rAF while the keyboard animates. A silently changed metric is applied;
    // a truly unchanged sample is discarded by the key guard above.
    const scheduleViewportSettleSync = () => {
      for (const timer of settleTimers) clearTimeout(timer);
      settleTimers = [];
      let cancelled = false;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!cancelled) updateViewport();
      }));
      for (const delay of TERMINAL_VIEWPORT_SETTLE_DELAYS_MS) {
        settleTimers.push(setTimeout(() => {
          if (!cancelled) updateViewport();
        }, delay));
      }
      return () => {
        cancelled = true;
        for (const timer of settleTimers) clearTimeout(timer);
        settleTimers = [];
      };
    };

    const resetBaseline = () => {
      baseline = null;
      updateViewport();
    };
    const viewport = window.visualViewport;
    const panel = panelRef.current;
    let cancelSettleSync: (() => void) | null = null;
    const onViewportChanged = () => {
      updateViewport();
      cancelSettleSync?.();
      cancelSettleSync = scheduleViewportSettleSync();
    };
    viewport?.addEventListener('resize', onViewportChanged);
    viewport?.addEventListener('scroll', onViewportChanged);
    viewport?.addEventListener('scrollend', onViewportChanged);
    window.addEventListener('resize', onViewportChanged);
    window.addEventListener('orientationchange', resetBaseline);
    document.addEventListener('fullscreenchange', onViewportChanged);
    panel?.addEventListener('focusin', onViewportChanged);
    panel?.addEventListener('focusout', onViewportChanged);
    media?.addEventListener?.('change', resetBaseline);
    const panelResizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(onViewportChanged);
    if (panel && panelResizeObserver) panelResizeObserver.observe(panel);
    updateViewport();
    cancelSettleSync = scheduleViewportSettleSync();

    return () => {
      viewport?.removeEventListener('resize', onViewportChanged);
      viewport?.removeEventListener('scroll', onViewportChanged);
      viewport?.removeEventListener('scrollend', onViewportChanged);
      window.removeEventListener('resize', onViewportChanged);
      window.removeEventListener('orientationchange', resetBaseline);
      document.removeEventListener('fullscreenchange', onViewportChanged);
      panel?.removeEventListener('focusin', onViewportChanged);
      panel?.removeEventListener('focusout', onViewportChanged);
      media?.removeEventListener?.('change', resetBaseline);
      panelResizeObserver?.disconnect();
      cancelSettleSync?.();
    };
  }, [isMaximized]);

  useEffect(() => {
    const updateFullscreenState = () => {
      // Escape should leave both browser fullscreen and the viewport fallback.
      // If fullscreen was declined there is no fullscreenchange event, so the
      // fallback remains available until the user presses the visible control.
      if (document.fullscreenElement !== panelRef.current) setIsMaximized(false);
    };
    document.addEventListener('fullscreenchange', updateFullscreenState);
    return () => document.removeEventListener('fullscreenchange', updateFullscreenState);
  }, []);

  useEffect(() => {
    if (!isMaximized) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isMaximized]);

  const toggleMaximize = useCallback(async () => {
    const panel = panelRef.current;
    if (!panel) return;

    if (isMaximized) {
      setIsMaximized(false);
      if (document.fullscreenElement === panel) {
        try {
          await document.exitFullscreen();
        } catch {
          // The fallback maximize state is already removed.
        }
      }
      return;
    }

    setIsMaximized(true);
    if (panel.requestFullscreen) {
      try {
        await panel.requestFullscreen();
      } catch {
        // Fullscreen needs a user gesture and can be disabled by browser
        // policy; fixed viewport maximize remains a useful accessible fallback.
      }
    }
  }, [isMaximized]);

  const activeViewport = isMaximized ? visualViewport : null;
  const terminalViewportStyle: CSSProperties | undefined = activeViewport
    ? {
      '--terminal-visual-viewport-height': `${activeViewport.height}px`,
      '--terminal-visual-viewport-width': `${activeViewport.width}px`,
      '--terminal-visual-viewport-offset-top': `${activeViewport.offsetTop}px`,
      '--terminal-visual-viewport-offset-left': `${activeViewport.offsetLeft}px`,
      '--terminal-layout-viewport-height': `${layoutViewportHeight ?? activeViewport.height}px`
    } as CSSProperties
    : undefined;

  return {
    panelRef: panelRef as RefObject<T>,
    isMaximized,
    isMobileKeyboardOpen: isMaximized && isMobileKeyboardOpen,
    terminalKeyboardState: isMaximized && isMobileKeyboardOpen ? 'open' : 'closed',
    terminalKeyboardOverdrawGuard: isMaximized ? keyboardOverdrawGuard : 0,
    terminalViewportFrameLayout: isMaximized ? viewportFrameLayout : 'overlay',
    visualViewport: activeViewport,
    terminalViewportStyle,
    toggleMaximize
  };
}
